import { MarketPlace, Placement, Template } from '@amzn/fire-tv-campaign-automation-service';
import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';

interface SelectionType {
  marketplace: MarketPlace | undefined;
  template: Template | undefined;
  placement: Placement | undefined;
  setMarketplace: Dispatch<SetStateAction<MarketPlace | undefined>>;
  setTemplate: Dispatch<SetStateAction<Template | undefined>>;
  setPlacement: Dispatch<SetStateAction<Placement | undefined>>;
}

const SelectionContext = createContext<SelectionType>({
  marketplace: undefined,
  template: undefined,
  placement: undefined,
  setMarketplace: () => {},
  setTemplate: () => {},
  setPlacement: () => {},
});

/**
 * Hook to provide selection state management
 * ToDo: Add cookie persistence logic here when needed:
 * - Check for the cookie that has the last selected values
 * - If the cookie is not present, use the default values
 * - If the cookie is present, use the values from the cookie
 */
const useSelectionState = () => {
  const [marketplace, setMarketplace] = useState<MarketPlace | undefined>(MarketPlace.US);
  const [template, setTemplate] = useState<Template | undefined>(Template.FR_READY_ASSETS);
  const [placement, setPlacement] = useState<Placement | undefined>(Placement.FEATURE_ROTATOR);

  return useMemo(
    () => ({
      marketplace,
      template,
      placement,
      setMarketplace,
      setTemplate,
      setPlacement,
    }),
    [marketplace, template, placement],
  );
};

const useSelection = (): SelectionType => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionContextProvider');
  }
  return context;
};

export { SelectionContext, useSelection, useSelectionState };
